import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { useInfinitePriceAlertEvents, useCreatePriceAlertConfig, usePriceAlertStats } from "@/hooks/usePriceAlerts";
import { PriceAlertEventTile } from "@/components/PriceAlertEventTile";
import { PriceAlertConfigDrawer } from "@/components/PriceAlertConfigDrawer";
import { PriceAlertAnalyzeDrawer } from "@/components/PriceAlertAnalyzeDrawer";
import { PositionTradeDrawer } from "@/components/PositionTradeDrawer";
import { CreatePriceAlertConfigPayload, PriceAlertEventData } from "@/api/price-alerts";
import { PriceAlertStatsCards } from "@/components/PriceAlertStatsCards";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import { Loader2, Plus, Settings } from "lucide-react";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { PageHeader } from "@/components/ui/PageHeader";
import { TabSelector } from "@/components/TabSelector";
import {
  filterPriceAlertEvents,
  TIMEFRAME_FILTERS,
  TYPE_OPTIONS,
  type TypeFilter,
} from "./price-alert-filters";

const EVENTS_PER_PAGE = 10;

export const PriceAlertsPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [analyzeEvent, setAnalyzeEvent] = useState<PriceAlertEventData | null>(null);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const { data: statsData, isLoading: isLoadingStats } = usePriceAlertStats(userId);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfinitePriceAlertEvents(userId, EVENTS_PER_PAGE);

  const createMutation = useCreatePriceAlertConfig(userId);

  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [timeframeFilter, setTimeframeFilter] = useState<string>("all");
  const events = filterPriceAlertEvents(
    data?.pages.flatMap((page) => page.events) ?? [],
    typeFilter,
    timeframeFilter
  );
  const hasMore = hasNextPage ?? false;

  useEffect(() => {
    if (!hasMore || isLoading || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [hasMore, isLoading, isFetchingNextPage, fetchNextPage]);

  const handleCreate = (payload: CreatePriceAlertConfigPayload) => {
    createMutation.mutate(payload, {
      onSuccess: () => setShowAddDrawer(false),
    });
  };

  const handleOpenTrade = (event: PriceAlertEventData) => {
    setTradeSymbol(ensureUsdtSymbol(event.symbol));
    setIsTradeDrawerOpen(true);
  };

  const handleTradeFromAnalyze = (symbol: string) => {
    setTradeSymbol(symbol);
    setAnalyzeEvent(null);
    setIsTradeDrawerOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          <div className="mb-4 flex flex-col gap-6 pt-2">
            <PageHeader
              title="Price"
              accent="Alerts"
              subtitle="Real-time price spike and drop notifications across global markets."
              actions={
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => navigate("/price-alerts/manage")}
                    className="glass flex size-10 items-center justify-center"
                    aria-label="Manage alerts"
                  >
                    <Settings className="size-5 text-[#d4d4d4]" />
                  </button>
                  <button
                    onClick={() => setShowAddDrawer(true)}
                    className="flex size-10 items-center justify-center rounded-xl bg-accent"
                    aria-label="Add price alert"
                  >
                    <Plus className="size-5 text-white" />
                  </button>
                </div>
              }
            />
  
            <PriceAlertStatsCards stats={statsData} isLoading={isLoadingStats} />
  
            <TabSelector
              options={TYPE_OPTIONS}
              value={typeFilter}
              onChange={setTypeFilter}
              className="self-start"
            />
            <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none]">
              <TabSelector
                options={TIMEFRAME_FILTERS}
                value={timeframeFilter}
                onChange={setTimeframeFilter}
                variant="compact"
                className="w-max min-w-full"
              />
            </div>
          </div>

          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          )}

          {!isLoading && events.length === 0 && (
            <div className="text-center py-12">
              <p className="text-text-secondary">No price alerts yet</p>
              <p className="text-text-secondary text-sm mt-1">
                Configure your alerts to start receiving notifications
              </p>
            </div>
          )}

          {!isLoading && (
            <div className="space-y-3">
              {events.map((event) => (
                <PriceAlertEventTile
                  key={event.id}
                  event={event}
                  onAnalyze={setAnalyzeEvent}
                  onTrade={handleOpenTrade}
                />
              ))}
            </div>
          )}

          {!isLoading && hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-8">
              {isFetchingNextPage && (
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
              )}
            </div>
          )}

          {!isLoading && !hasMore && events.length > 0 && (
            <div className="text-center py-8">
              <p className="text-text-secondary text-sm">
                No more alerts to load
              </p>
            </div>
          )}
        </div>
      </div>

      <ReferralBottomNav />

      {/* Add Alert Drawer */}
      <PriceAlertConfigDrawer
        open={showAddDrawer}
        onOpenChange={setShowAddDrawer}
        onSubmit={(payload) =>
          handleCreate(payload as CreatePriceAlertConfigPayload)
        }
        isLoading={createMutation.isPending}
      />

      {/* Analyze Drawer */}
      <PriceAlertAnalyzeDrawer
        userId={userId}
        event={analyzeEvent}
        open={!!analyzeEvent}
        onOpenChange={(open) => { if (!open) setAnalyzeEvent(null); }}
        onTrade={handleTradeFromAnalyze}
      />

      {/* Trade Drawer */}
      <PositionTradeDrawer
        userId={userId}
        symbol={tradeSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => {
          setIsTradeDrawerOpen(open);
          if (!open) setTradeSymbol(null);
        }}
        onGoToPosition={() => {}}
      />
    </div>
  );
};
